/**
 * Shared owner order → journey step mapping (same rules as OwnerOrdersFlowRow).
 */

export const OWNER_FLOW_STEPS = [
  { key: 'quote', label: 'Lab review', icon: '📋' },
  { key: 'payment', label: 'Payment', icon: '💰' },
  { key: 'ship_to_grader', label: 'Ship to grader', icon: '📦' },
  { key: 'received', label: 'Received', icon: '📬' },
  { key: 'grading', label: 'Grading', icon: '🔍' },
  { key: 'graded', label: 'Graded', icon: '⭐' },
  { key: 'sent_to_lab', label: 'Sent to Lab', icon: '📤' },
  { key: 'received_by_mint_lab', label: 'Received by Mint Lab', icon: '🏁' },
  { key: 'ready_for_minting', label: 'Ready for minting', icon: '⚡' },
  { key: 'in_progress', label: 'In progress', icon: '⏳' },
  { key: 'minted', label: 'Minted', icon: '⚡' },
  { key: 'nfc_info_added', label: 'NFC info added', icon: '📲' },
  { key: 'sent_to_owner', label: 'Sent to owner', icon: '🚚' },
  { key: 'received_by_owner', label: 'Received by owner', icon: '🏆' },
]

export function getCurrentStep(order) {
  const status = order?.status
  const hasSentToLab = !!order?.shippedToLabAt || !!order?.labTrackingNumber
  const labReceived = !!order?.labReceivedAt
  const hasDraft = !!order?.hasLabMediaDraft

  if (!status || status === 'PENDING_QUOTE') return 'quote'
  if (['QUOTE_SENT', 'AWAITING_PAYMENT', 'PAYMENT_PENDING', 'PRICE_SET'].includes(status))
    return 'payment'
  // Paid but not yet shipped: next action is ship to grader (do NOT fall through to in_progress)
  if (status === 'PAID') return 'ship_to_grader'
  if (status === 'AWAITING_SHIPMENT' || status === 'SHIPPED_BY_OWNER') return 'ship_to_grader'
  if (status === 'CARD_RECEIVED') return 'received'
  if (status === 'IN_PROGRESS') return 'grading'
  if (status === 'COMPLETED') {
    if (!hasSentToLab) return 'graded'
    if (!labReceived) return 'sent_to_lab'
    if (hasDraft) return 'in_progress'
    return 'ready_for_minting'
  }
  if (status === 'MINTING') return 'in_progress'
  if (status === 'NFT_MINTED' || status === 'MINTED')
    return order?.nfcInfoAddedAt ? 'minted' : 'nfc_info_added'
  if (status === 'SHIPPED_TO_OWNER') return 'sent_to_owner'
  if (status === 'DELIVERED') return 'received_by_owner'
  return 'in_progress'
}

export function isStepDone(order, stepKey) {
  const status = order?.status
  const hasSentToLab = !!order?.shippedToLabAt || !!order?.labTrackingNumber
  const labReceived = !!order?.labReceivedAt
  const hasDraft = !!order?.hasLabMediaDraft
  const mintedOrBeyond = ['NFT_MINTED', 'MINTED', 'SHIPPED_TO_OWNER', 'DELIVERED'].includes(status)
  const shippedOrDelivered = status === 'SHIPPED_TO_OWNER' || status === 'DELIVERED'
  const nfcAdded = !!order?.nfcInfoAddedAt || shippedOrDelivered
  const completedWithLabReceived = status === 'COMPLETED' && labReceived
  const completedInDraft = completedWithLabReceived && hasDraft
  const completedReady = completedWithLabReceived && !hasDraft

  if (stepKey === 'quote') return true
  if (stepKey === 'payment') {
    return [
      'QUOTE_SENT',
      'AWAITING_PAYMENT',
      'PAYMENT_PENDING',
      'PRICE_SET',
      'PAID',
      'AWAITING_SHIPMENT',
      'SHIPPED_BY_OWNER',
      'CARD_RECEIVED',
      'IN_PROGRESS',
      'COMPLETED',
      'MINTING',
      'NFT_MINTED',
      'MINTED',
      'SHIPPED_TO_OWNER',
      'DELIVERED',
    ].includes(status)
  }
  if (stepKey === 'ship_to_grader') {
    // AWAITING_SHIPMENT = owner must still ship; do not mark this step done until tracking is submitted.
    return [
      'SHIPPED_BY_OWNER',
      'CARD_RECEIVED',
      'IN_PROGRESS',
      'COMPLETED',
      'MINTING',
      'NFT_MINTED',
      'MINTED',
      'SHIPPED_TO_OWNER',
      'DELIVERED',
    ].includes(status)
  }
  if (stepKey === 'received') {
    return [
      'CARD_RECEIVED',
      'IN_PROGRESS',
      'COMPLETED',
      'MINTING',
      'NFT_MINTED',
      'MINTED',
      'SHIPPED_TO_OWNER',
      'DELIVERED',
    ].includes(status)
  }
  if (stepKey === 'grading') {
    return [
      'IN_PROGRESS',
      'COMPLETED',
      'MINTING',
      'NFT_MINTED',
      'MINTED',
      'SHIPPED_TO_OWNER',
      'DELIVERED',
    ].includes(status)
  }
  if (stepKey === 'graded') {
    return [
      'COMPLETED',
      'MINTING',
      'NFT_MINTED',
      'MINTED',
      'SHIPPED_TO_OWNER',
      'DELIVERED',
    ].includes(status)
  }
  if (stepKey === 'sent_to_lab') {
    return status === 'COMPLETED'
      ? hasSentToLab || completedWithLabReceived || completedInDraft || completedReady
      : ['MINTING', 'NFT_MINTED', 'MINTED', 'SHIPPED_TO_OWNER', 'DELIVERED'].includes(status)
  }
  if (stepKey === 'received_by_mint_lab') {
    return (
      completedWithLabReceived ||
      ['MINTING', 'NFT_MINTED', 'MINTED', 'SHIPPED_TO_OWNER', 'DELIVERED'].includes(status)
    )
  }
  if (stepKey === 'ready_for_minting') {
    return (
      completedReady ||
      completedInDraft ||
      ['MINTING', 'NFT_MINTED', 'MINTED', 'SHIPPED_TO_OWNER', 'DELIVERED'].includes(status)
    )
  }
  if (stepKey === 'in_progress') {
    return (
      completedInDraft ||
      ['MINTING', 'NFT_MINTED', 'MINTED', 'SHIPPED_TO_OWNER', 'DELIVERED'].includes(status)
    )
  }
  if (stepKey === 'minted') return mintedOrBeyond
  if (stepKey === 'nfc_info_added') return nfcAdded
  if (stepKey === 'sent_to_owner') return shippedOrDelivered
  if (stepKey === 'received_by_owner') return status === 'DELIVERED'
  return false
}
